import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { LogbookTemplatesService } from './logbook-templates.service';
import * as LogbookTemplateActions from './logbook-templates.actions';
import * as logbookAppReducer from '../../logbook.reducer';
import * as AppActions from '../../app/actions';
import { IGetManyResponse } from '../../../shared/model/interface/crud-response-interface.model';
import { IFieldVersion } from '../../../view/reports/form-templates/form-templates.model';
import * as UsersActions from '../../settings/users/users.actions';
import _ from 'lodash';
import { AdvancedFilterService } from '../../../shared/component/filter/advanced-filter/advanced-filter.service';
import { emptyAction } from '../../../../constants';
import { FormMasterDataService } from '../../settings/form-master-data/form-master-data.service';
import { IFormVersionHistory } from '../../forms/forms.model';
import { FormsService } from '../../forms/forms.service';
import { ILogbookTemplateData } from '../../../view/reports/logbook-templates/logbook-templates.model';
import { IGetLogbookTemplates } from './logbook-templates.model';
import { IWorkflowStepsData } from '../../../view/settings/workflows/workflows.model';
import { WorkflowsService } from '../../settings/workflows/workflows.service';
import { ServiceUtilities } from '../../../shared/helper/service-utilities';

@Injectable()
export class LogbookTemplatesEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: LogbookTemplatesService,
    private readonly formService: FormsService,
    private readonly advancedFilterService: AdvancedFilterService,
    private readonly formMasterDataService: FormMasterDataService,
    private readonly workflowService: WorkflowsService,
    private readonly store: Store<logbookAppReducer.LogbookAppState>,
  ) {}

  getAllMasterData = createEffect(() =>
    this.actions$.pipe(
      ofType(LogbookTemplateActions.GET_ALL_MASTER_DATA),
      switchMap((payload: LogbookTemplateActions.GetAllMasterData) => {
        this.store.dispatch(new AppActions.ShowLoader());

        let body: Record<string, string | number> = {};

        if (payload.formIds) {
          body = { ...body, form_ids: JSON.stringify(payload.formIds) };
        }

        if (payload.logbookIds) {
          body = { ...body, logbook_ids: JSON.stringify(payload.logbookIds) };
        }

        if (payload.sourceObjectDate) {
          body = { ...body, source_object_date: JSON.stringify(payload.sourceObjectDate) };
        }

        return this.service.getAllMasterData(body).pipe(
          switchMap((response: IGetManyResponse<IFieldVersion>) => {
            return of(
              new LogbookTemplateActions.GetAllMasterDataCompleted(response),
              payload.dispatchHideLoader
                ? new AppActions.HideLoader()
                : new LogbookTemplateActions.GetAllMasterDataCompletedToZip(),
            );
          }),
          catchError((errorRes) => {
            return of(new LogbookTemplateActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new LogbookTemplateActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getFormTemplates = createEffect(() =>
    this.actions$.pipe(
      ofType(LogbookTemplateActions.GET_LOGBOOK_TEMPLATES),
      switchMap((payload: LogbookTemplateActions.GetLogbookTemplates) => {
        this.store.dispatch(new AppActions.ShowLoader());

        let body: Record<string, string | number> = ServiceUtilities.prepareGenericBodyForRequest({
          page: payload.tableQuery?.page ?? 1,
          sort: payload.tableQuery?.sort,
          rowsPerPage: payload.tableQuery?.rowsPerPage ?? 10,
          ...(!_.isNil(payload.tableQuery?.selectedForms) &&
            Array.isArray(payload.tableQuery?.selectedForms) && {
              forms: JSON.stringify(payload.tableQuery?.selectedForms),
            }),
          ...(!_.isNil(payload.tableQuery?.selectedLogbooks) &&
            Array.isArray(payload.tableQuery?.selectedLogbooks) && {
              logbooks: JSON.stringify(payload.tableQuery?.selectedLogbooks),
            }),
          ...(!_.isNil(payload.tableQuery?.selectedDateRange) && {
            date: JSON.stringify(payload.tableQuery.selectedDateRange),
          }),
          ...(!_.isNil(payload.tableQuery.selectedStatuses) &&
            Array.isArray(payload.tableQuery.selectedStatuses) && {
              statuses: JSON.stringify(payload.tableQuery.selectedStatuses),
            }),
        });

        if (!_.isEmpty(payload.tableQuery.advancedFilter)) {
          body = { ...body, advanced_filter: JSON.stringify(this.service.getSearchString(payload.tableQuery)) };
        }

        return this.service.getLogbookTemplates(body).pipe(
          switchMap((response: IGetManyResponse<ILogbookTemplateData>) => {
            return of(
              new LogbookTemplateActions.GetLogbookTemplatesCompleted(response),
              payload.dispatchHideLoader
                ? new AppActions.HideLoader()
                : new LogbookTemplateActions.GetLogbookTemplatesCompletedToZip(),
            );
          }),
          catchError((errorRes) => {
            return of(new LogbookTemplateActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new LogbookTemplateActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getFormTemplateData = createEffect(() =>
    this.actions$.pipe(
      ofType(LogbookTemplateActions.GET_LOGBOOK_TEMPLATE_DATA_LOADING),
      switchMap((payload: LogbookTemplateActions.GetLogbookTemplatesData) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getLogbookTemplateData(payload.id).pipe(
          switchMap((response: IGetLogbookTemplates) => {
            return of(new LogbookTemplateActions.GetLogbookTemplatesDataLoaded(response), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new LogbookTemplateActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new LogbookTemplateActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  downloadExcel = createEffect(() =>
    this.actions$.pipe(
      ofType(LogbookTemplateActions.DOWNLOAD_LOGBOOK_TEMPLATES_EXCEL),
      switchMap((objectData: LogbookTemplateActions.DownloadLogbookTemplatesExcel) => {
        this.store.dispatch(new AppActions.ShowLoader());
        this.service.downloadExcel(
          objectData.filters,
          objectData.columns,
          objectData.approvalStatuses,
          objectData.rawMasterDataColumns,
        );

        return emptyAction;
      }),
      catchError((error) => {
        return of(new UsersActions.FetchError(error), new AppActions.HideLoader());
      }),
    ),
  );

  getMasterData = createEffect(() =>
    this.actions$.pipe(
      ofType(LogbookTemplateActions.GET_MASER_DATA),
      switchMap((payload: LogbookTemplateActions.GetMasterDataLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.formMasterDataService.getFormMasterDetailData(payload.id).pipe(
          switchMap((response) => {
            return of(
              new LogbookTemplateActions.GetMasterDataLoaded({
                success: response[0].success && response[1].success,
                data: { ...response[0].data, fields: response[1].data ?? [] },
              }),
            );
          }),
          catchError((errorRes) => {
            return of(new LogbookTemplateActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new LogbookTemplateActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getFormHistory = createEffect(() =>
    this.actions$.pipe(
      ofType(LogbookTemplateActions.GET_LOGBOOK_TEMPLATE_HISTORY),
      switchMap((payload: LogbookTemplateActions.GetLogbookTemplatesHistoryLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getFormTemplatesHistory(payload.id).pipe(
          switchMap((response: IGetManyResponse<IFormVersionHistory>) => {
            return of(
              new LogbookTemplateActions.GetLogbookTemplatesHistoryLoaded(response),
              new AppActions.HideLoader(),
            );
          }),
          catchError((errorRes) => {
            return of(new LogbookTemplateActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new LogbookTemplateActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getWorkFlowStepData = createEffect(() =>
    this.actions$.pipe(
      ofType(LogbookTemplateActions.WORKFLOW_STEP_DATA_LOADING),
      switchMap((payload: LogbookTemplateActions.GetWorkflowStepData) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.workflowService.getWorkflowsSteps(payload.id).pipe(
          switchMap((response: IGetManyResponse<IWorkflowStepsData>) => {
            return of(new LogbookTemplateActions.GetWorkflowStepDataLoaded(response), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new LogbookTemplateActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new LogbookTemplateActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );
}
